/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent, SUCCESS_DIALOG_TITLE } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { AblenetService, ValidationInfoDto } from "app/services/ablenet.service";

/** User enters the Shipment Number on this Page & validates it */
@Component({
  selector: 'ablenet-shipment-validation',
  templateUrl: './ablenet.shipment-validation.enter-info.html',
  styleUrls: ['./ablenet.shipment-validation.enter-info.css']
})
export class AbleNetShipmentValidationEnterInfoComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) 
  indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Portal Service selected by User which will be performing the validation */
  portal: ServiceInstance;

  /** Flag used to disable the UI while performing background tasks */
  isUiDisabled: boolean = false;

  /** Shipment Number entered by User */
  shipmentNbr: string = '';

  /** Constructor */
  constructor(
    private ablenetService: AblenetService,
    private route: ActivatedRoute,
    private router: Router  ){}

  /** Initializes the component. Called by the Angular Component Lifecycle. */
  async ngOnInit() {
    console.log("Initializing Ablenet - Shipment Validation Enter-Info Component");
    // Get the data that was resolved in the background during the page transition
    this.route.data.subscribe(
      (data: {
        portal: ServiceInstance
      }) => {
        console.log(`Obtained Portal: '${data?.portal?.id}/${data?.portal?.name}'`);
        this.portal = data?.portal;
      })
  }

  /**
   * Initializes the component view.  
   * Called automatically by the Angular Component Lifecycle.
   * 
   * Used to auto-focus on the "Order Name" input field.  
   */
  ngAfterViewInit(): void {
    // Auto-focus on the "Order Name" input (Not using the "[autofocus]" as that only works the 1st time the page is displayed, not after migrating back after clearing / processing an order)
    const element = document.getElementById("dopv-orderName");
    if (element != null && element != undefined) {
      element.focus();
    }
  }

    /**
   * Called when the "Validate" Button is clicked in the UI.
   * Calls the Server to get the "Order Information" for the information
   * entered in the UI. 
   */
  onValidateClicked(): void {
    if (!this.isUiDisabled) {
      this.isUiDisabled = true;
      this.indefiniteProgressDialog.show("Processing", "Validating Shipment info...");
      this.validateShipment();
    }
  }

  // Send the Request to backend for validation & return the Validation Info - shipmentID & PackageID
  async validateShipment(): Promise<void> {
    let shipmentValidationInfo: ValidationInfoDto = null;
    try {
          shipmentValidationInfo = await this.ablenetService.validateShipment(this.portal.id, this.shipmentNbr);
        } catch (error) {
          this.indefiniteProgressDialog.hide();
          this.isUiDisabled = false;
          this.handleError(error);
          return;
        }

        this.isUiDisabled = false;
        this.indefiniteProgressDialog.hide();
        console.log(`Shipment Details found for Shipment Nbr : ${JSON.stringify(shipmentValidationInfo)}`);

        // Once validated verify data & naviagte to dashboard
        if (shipmentValidationInfo?.attemptDto?.shipmentValidationInfo?.packageId != null && shipmentValidationInfo?.attemptDto?.shipmentValidationInfo?.shipmentId != null) {
          this.router.navigate(['ablenet', 'shipment-validation', this.portal?.id, 'dashboard'],{} );
        }
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}

